import { useCallback, useEffect, useState } from 'react';
import {
    LuBox, LuCalendar, LuCircleAlert, LuCreditCard, LuGift,
    LuLoaderCircle, LuQrCode, LuArrowUpRight, LuUsers,
} from 'react-icons/lu';
import aviso, { textoDoErro } from '../lib/aviso';
import PageHeader from '../components/PageHeader';
import Dialog from '../components/Dialog';
import PlanoModal from '../components/PlanoModal';
import { ehAdministradorDaEmpresa } from '../config/permissions';
import {
    billingService, motivoSemTroca, rotuloProvedor,
    type AssinaturaBackend,
} from '../services/billingService';
/* O CSS antigo desta tela continua importado de propósito: nenhuma classe dele
   é usada aqui, mas apagar arquivo é decisão dele — ficou como órfão a limpar. */
import './BillingPlanPage.css';
import '../components/PlanoModal.css';

/**
 * Meu plano — a ficha da assinatura.
 *
 * Desenho fechado de 2026-09-12. Ela é **enxuta de propósito**: ele disse que
 * "provavelmente no futuro será desabilitada", então não recebe nada que
 * precise ser reescrito em outro lugar depois. Toda a lógica de escolher e
 * contratar mora no `PlanoModal`, e a página só o chama.
 */

const brl = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const data = (iso: string) => new Date(iso).toLocaleDateString('pt-BR');

const CICLO: Record<string, string> = {
    WEEKLY: 'semana', MONTHLY: 'mês', QUARTERLY: 'trimestre',
    SEMIANNUALLY: 'semestre', ANNUALLY: 'ano', Gratuito: 'gratuito',
};

/** Os cinco do enum da API. Fora deles a tela não imprime o código cru — a base
 *  de homologação já devolveu `ACCEP`, que o enum não produz. */
const SITUACAO: Record<string, { txt: string; tom: 'ok' | 'warn' | 'bad' }> = {
    TRIAL: { txt: 'Período de teste', tom: 'warn' },
    PENDING: { txt: 'Aguardando pagamento', tom: 'warn' },
    PAID: { txt: 'Ativa', tom: 'ok' },
    CANCELLED: { txt: 'Cancelada', tom: 'bad' },
    EXPIRED: { txt: 'Expirada', tom: 'bad' },
};

const BillingPlanPage = () => {
    const [assinatura, setAssinatura] = useState<AssinaturaBackend | null>(null);
    const [carregando, setCarregando] = useState(true);
    const [modalAberto, setModalAberto] = useState(false);
    const [confirmarCancelar, setConfirmarCancelar] = useState(false);
    const [cancelando, setCancelando] = useState(false);

    const ehAdmin = ehAdministradorDaEmpresa();

    const carregar = useCallback(async () => {
        setCarregando(true);
        try {
            const res = await billingService.buscarAssinaturaAtiva();
            setAssinatura(res?.sucesso ? res.dados : null);
        } catch {
            aviso.erro('Não foi possível carregar a assinatura.');
        } finally {
            setCarregando(false);
        }
    }, []);

    useEffect(() => { carregar(); }, [carregar]);

    const cancelar = async () => {
        setConfirmarCancelar(false);
        setCancelando(true);
        try {
            const res = await billingService.cancelarAssinatura();
            if (res.sucesso) {
                aviso.ok(res.mensagem || 'Assinatura cancelada. O acesso foi encerrado imediatamente.');
                carregar();
            } else {
                aviso.erro(res.mensagem || 'Não foi possível cancelar.');
            }
        } catch (e: any) {
            aviso.erro(textoDoErro(e, 'Não foi possível cancelar.'));
        } finally {
            setCancelando(false);
        }
    };

    const ehTrial = assinatura?.assinaturaStatus === 'TRIAL';
    const cancelada = assinatura?.assinaturaStatus === 'CANCELLED';
    const situacao = assinatura ? SITUACAO[assinatura.assinaturaStatus] : undefined;
    const impedimento = motivoSemTroca(assinatura?.assinaturaMetodo);

    const metodo = !assinatura ? null
        : assinatura.assinaturaMetodo === 'PIX' ? 'PIX (pagamento avulso por ciclo)'
        : assinatura.assinaturaMetodo === 'CARD' ? 'Cartão de crédito (recorrente)'
        : assinatura.assinaturaMetodo === 'CARD_INSTALLMENT' ? 'Cartão parcelado (compra única, não renova)'
        : assinatura.assinaturaMetodo;

    return (
        <div className="page-shell pln-screen">
            <PageHeader
                centered
                title="Meu plano"
                subtitle="Assinatura, cobrança e troca de plano."
            />
            <div className="page-body">
                <div className="pln-col">
                    {carregando ? (
                        <div className="pln-carregando">
                            <LuLoaderCircle size={18} className="pln-girando" /> Carregando…
                        </div>
                    ) : !ehAdmin ? (
                        <div className="pln-vazio">
                            <LuBox size={22} />
                            <b>Assinatura é coisa de administrador</b>
                            Fale com o administrador da sua empresa para ver ou mudar o plano.
                        </div>
                    ) : !assinatura ? (
                        <div className="pln-vazio">
                            <LuCircleAlert size={22} />
                            <b>Nenhuma assinatura ativa</b>
                            Sua empresa está sem plano. Contrate um para reativar o acesso.
                            <div className="pln-vazio-bt">
                                <button type="button" className="pln-btn is-pri"
                                    onClick={() => setModalAberto(true)}>
                                    <LuBox size={14} /> Ver planos
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="pln-ficha">
                                <div className="pln-ficha-top">
                                    <span className="pln-mk"><LuBox size={18} /></span>
                                    <div className="pln-ficha-id">
                                        <div className="pln-ficha-nm">{assinatura.nomePlano || 'Plano'}</div>
                                        <div className="pln-ficha-vl">
                                            {ehTrial ? 'Gratuito' : (
                                                <>{brl(assinatura.assinaturaValor)}
                                                    {' por '}{CICLO[assinatura.assinaturaCiclo] ?? 'ciclo'}</>
                                            )}
                                        </div>
                                    </div>
                                    {situacao
                                        ? <span className={`pln-sit is-${situacao.tom}`}>{situacao.txt}</span>
                                        : <span className="pln-sit is-warn" title={assinatura.assinaturaStatus}>
                                            Situação indefinida
                                          </span>}
                                </div>

                                <div className="pln-linhas">
                                    {assinatura.assinaturaExpiraEm && (
                                        <div className="pln-linha">
                                            <LuCalendar size={14} />
                                            {/* Cancelar não mexe em `assinaturaExpiraEm`, só encerra o
                                                acesso — sem este caso a tela anunciaria "próxima
                                                renovação" para quem já perdeu o acesso. */}
                                            <span className="pln-k">
                                                {cancelada ? 'Assinatura cancelada'
                                                    : ehTrial ? 'Fim do período de teste'
                                                    : 'Próxima renovação'}
                                            </span>
                                            <span className="pln-v">
                                                {cancelada ? 'Acesso encerrado' : data(assinatura.assinaturaExpiraEm)}
                                            </span>
                                        </div>
                                    )}
                                    <div className="pln-linha">
                                        <LuUsers size={14} />
                                        <span className="pln-k">Usuários contratados</span>
                                        <span className="pln-v">{assinatura.assinaturaQuantidadeUsuarios ?? 1}</span>
                                    </div>
                                    <div className="pln-linha">
                                        {assinatura.assinaturaMetodo === 'PIX' ? <LuQrCode size={14} />
                                            : ehTrial ? <LuGift size={14} />
                                            : <LuCreditCard size={14} />}
                                        <span className="pln-k">Forma de pagamento</span>
                                        <span className="pln-v">
                                            {metodo}
                                            {!ehTrial && assinatura.assinaturaProvedorPagamento
                                                && <> · via {rotuloProvedor(assinatura.assinaturaProvedorPagamento)}</>}
                                        </span>
                                    </div>
                                    {assinatura.assinaturaInicioEm && (
                                        <div className="pln-linha">
                                            <LuCalendar size={14} />
                                            <span className="pln-k">Cliente desde</span>
                                            <span className="pln-v">{data(assinatura.assinaturaInicioEm)}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {impedimento && (
                                <div className="pln-nota">
                                    <LuCircleAlert size={14} /><span>{impedimento}</span>
                                </div>
                            )}

                            <div className="pln-acoes">
                                {assinatura.assinaturaStatus === 'PENDING' && assinatura.assinaturaCheckoutUrl && (
                                    <a className="pln-btn is-pri pln-btn-auto"
                                        href={assinatura.assinaturaCheckoutUrl}
                                        target="_blank" rel="noopener noreferrer">
                                        <LuArrowUpRight size={14} /> Concluir pagamento
                                    </a>
                                )}
                                <button type="button" className="pln-btn pln-btn-auto"
                                    onClick={() => setModalAberto(true)}>
                                    <LuBox size={14} /> {cancelada ? 'Contratar um plano' : 'Trocar de plano'}
                                </button>
                            </div>

                            {!cancelada && !ehTrial && (
                                <div className="pln-rodape">
                                    <button type="button" className="pln-btn pln-btn-auto is-danger"
                                        disabled={cancelando}
                                        onClick={() => setConfirmarCancelar(true)}>
                                        Cancelar assinatura
                                    </button>
                                    <span className="pln-rodape-nota">
                                        O acesso é encerrado imediatamente, sem reembolso do período já pago.
                                    </span>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            <PlanoModal open={modalAberto} onClose={() => setModalAberto(false)} onMudou={carregar} />

            <Dialog
                open={confirmarCancelar}
                onClose={() => setConfirmarCancelar(false)}
                onConfirm={cancelar}
                title={`Cancelar a assinatura do plano «${assinatura?.nomePlano ?? ''}»?`}
                confirmLabel="Cancelar assinatura"
                cancelLabel="Manter assinatura"
                danger
                busy={cancelando}
            >
                O acesso à plataforma é encerrado imediatamente, sem reembolso do período já pago.
            </Dialog>
        </div>
    );
};

export default BillingPlanPage;
